import { useState } from 'react'
import { createAppSession } from '../lib/api'

// Shared "New work area" modal — used by the App Details Sessions panel and
// the workspace header's work-area dropdown, so both creation paths have the
// exact same named-input flow and validation.
export default function CreateWorkAreaModal({
  appName,
  onClose,
  onCreated,
}: {
  appName: string
  onClose: () => void
  onCreated: (name: string) => void
}) {
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return

    setCreating(true)
    setError(null)

    const finalName = name.trim().toLowerCase()
    try {
      await createAppSession(appName, finalName)
      onCreated(finalName)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create work area')
    } finally {
      setCreating(false)
    }
  }

  const isValidName = /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/.test(name)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-md p-6">
        <h2 className="text-xl font-semibold mb-1">New work area</h2>
        <p className="text-sm text-muted-foreground mb-6">
          Create a separate place to try changes before bringing them back.
        </p>

        <form onSubmit={handleCreate}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Work area name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.toLowerCase())}
              placeholder="e.g., fix-login-bug"
              className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              autoFocus
            />
            {name && !isValidName && (
              <p className="text-xs text-destructive mt-1">
                Use lowercase letters, numbers, and hyphens only. Must start and end with alphanumeric.
              </p>
            )}
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm mb-4">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating || !name.trim() || !isValidName}
              className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
